import type { MouseEvent } from 'react'
import { useState, type FC } from 'react'

import {
  AssetFallbackIcon,
  Button,
  Card,
  LoaderCircle,
  Popup,
  StyledText,
  Tooltip,
} from '@skytech/ui'
import { ImageWithIconFallback } from '@skytech/ui/Image'
import { inStockStatusStyle } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'
import ChevronRightIcon from 'mdi-react/ChevronRightIcon'
import HeadphonesIcon from 'mdi-react/HeadphonesIcon'
import InformationCircleIcon from 'mdi-react/InformationCircleIcon'
import UnfoldMoreHorizontalIcon from 'mdi-react/UnfoldMoreHorizontalIcon'
import { twJoin, twMerge } from 'tailwind-merge'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import Highlight from '@/components/Highlight'
import { parseInStock } from '@/modules/ecom-policy/helpers'
import {
  usePolicyEditAccess,
  usePolicyId,
  useUpdateProduct,
} from '@/modules/ecom-policy/policy/hooks'
import { usePolicyFilters } from '@/modules/ecom-policy/policy/Policy.context'
import {
  handleMultiProductSelect,
  updatedProductAlias,
  updatedProductAliasDescription,
} from '@/modules/ecom-policy/policy/products/helpers'
import { ProductGroupOverview } from '@/modules/ecom-policy/policy/products/ProductGroupOverview'
import queries from '@/modules/ecom-policy/queries'
import { ecomPolicyResources } from '@/modules/ecom-policy/resources'
import {
  borderStyleByWhiteLabel,
  cardLoaderCircleStyleByWhiteLabel,
  fillExpiredColorByWhiteLabel,
} from '@/modules/ecom-policy/style-constant'

import type { PolicyProductCardProps } from './helper'
import type { ProductMoreInfoProps } from './ProductInfoPopup'
import { ProductMoreInfoOverview } from './ProductInfoPopup'

export const PolicyProductCard: FC<
  PolicyProductCardProps & {
    showNextPage?: () => void
    setMoreInfoProduct?: (product: ProductMoreInfoProps) => void
  }
> = ({
  id,
  name,
  price,
  description,
  imgUrl,
  currency,
  vatPct,
  inStock,
  isSelected,
  isExpired,
  isMandatory,

  priceId,
  quantityLimit,
  alias,
  aliasDescription,
  assetType,
  code,
  sku,
  manufacturerProductNo,
  manufacturerName,
  webUrl,

  groupedProducts,
  category,
  hasAccessories,

  isGrouped,
  showNextPage,
  setMoreInfoProduct,
}) => {
  const translate = useTranslation()
  const { whiteLabel } = useAppContext()
  const hasEditAccess = usePolicyEditAccess()
  const { updateProduct, isUpdating } = useUpdateProduct()
  const policyId = usePolicyId()
  const filters = usePolicyFilters()

  const [isProductsModalOpen, setIsProductsModalOpen] = useState(false) // for grouped
  const [isSettingsPopupOpen, setIsSettingsPopupOpen] = useState(false)

  const [searchQuery] = filters.searchQuery
  const [isVatIncluded] = filters.isVatIncluded
  const [, setCurrentlyBrowsedGroupKey] = filters.currentlyBrowsedGroupKey
  const setProductsWithVisibleAccessoriesIds = filters.setProductsWithVisibleAccessoriesIds

  const selectedProductsNumber = isGrouped
    ? groupedProducts?.filter(({ isSelected }) => isSelected).length
    : 0
  const isSingleProductGroup = isGrouped ? groupedProducts?.length === 1 : true
  const isFromPriceTextVisible = isGrouped
    ? isSingleProductGroup || selectedProductsNumber !== 1
    : false
  const product = isGrouped
    ? groupedProducts?.[0]
    : {
        id,
        name,
        priceId,
        desc: description,
        imgUrl,
        price,
        inStock,
        vatPct,
        isSelected: isSelected ? 1 : 0,
        isExpired: isExpired ? 1 : 0,
        isMandatory: isMandatory ? 1 : 0,
        quantityLimit,
        alias,
        aliasDescription,
        currency,
        assetType,
        code,
        sku,
        manufacturerProductNo,
        manufacturerName,
        webUrl,
      }

  const { data: policy } = useQuery(queries.policyDetails(policyId), () =>
    ecomPolicyResources.getPolicyDetails(policyId)
  )
  const { data } = useQuery(
    queries.policyProducts(policyId),
    () => {
      if (!policy) throw Error('Policy data is missing!')
      return ecomPolicyResources.getPolicyProducts(policyId, policy.vendorId)
    },
    { enabled: !!policy }
  )
  if (!data?.products) return null

  const isIconsVisible = isGrouped
    ? isExpired || isSelected || (isSingleProductGroup && Boolean(hasAccessories))
    : isExpired || isSelected

  const onSelectDeselectClick = () => {
    if (!hasEditAccess) {
      return
    }
    if (!isSingleProductGroup) {
      handleMultiProductSelect({
        updateProduct,
        groupedProducts,
        isAllSelected: selectedProductsNumber === groupedProducts?.length,
        policyId,
      })
      return
    }

    const addRemoveProduct = {
      isSelected: isSelected ? false : !product?.isSelected,
      quantityLimit: isSelected ? product?.quantityLimit || 1 : product?.quantityLimit,
      alias: isSelected ? updatedProductAlias(product) : product?.alias,
      aliasDescription: isSelected
        ? updatedProductAliasDescription(product)
        : product?.aliasDescription,
    }
    // Add/Remove product mutation
    updateProduct({
      policyId,
      payload: {
        products: [
          {
            productId: Number(product?.id),
            isSelected: addRemoveProduct.isSelected,
            priceId: product?.priceId || null,
            isMandatory,
            quantityLimit: addRemoveProduct.quantityLimit || 1,
            alias: addRemoveProduct.alias || null,
            aliasDescription: addRemoveProduct.aliasDescription || null,
          },
        ],
      },
    })
  }

  const onMoreInfoClick = () => {
    if (!isGrouped && showNextPage && setMoreInfoProduct && product) {
      showNextPage()
      setMoreInfoProduct({
        isSelected: isSelected,
        policyId: policyId,
        productId: Number(product.id),
      })
      return
    }
    if (isSingleProductGroup) {
      setIsSettingsPopupOpen(true)
      return
    }
    setIsProductsModalOpen(true)
  }

  const handleOnlySelectedProductAccessoriesClick = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation()

    const selectedGroupedProductsIds = groupedProducts?.map(({ id }) => id) || null
    setProductsWithVisibleAccessoriesIds(selectedGroupedProductsIds)
    setCurrentlyBrowsedGroupKey(String(id))
  }

  return (
    <Card
      noMargin
      className={twMerge(
        'hover:shadow-basic-black focus:shadow-basic-black shadow-basic relative flex flex-col items-center justify-between overflow-hidden rounded-lg border-2 border-solid bg-transparent px-4 py-2.5 transition duration-200 ease-out',
        isSelected &&
          (borderStyleByWhiteLabel[whiteLabel] || borderStyleByWhiteLabel.skytech)?.selected,
        isMandatory &&
          (borderStyleByWhiteLabel[whiteLabel] || borderStyleByWhiteLabel.skytech)?.mandatory,
        isExpired &&
          isSelected &&
          (borderStyleByWhiteLabel[whiteLabel] || borderStyleByWhiteLabel.skytech)?.expired,
        isExpired && !isSelected && 'border-transparent',
        !isExpired && !isSelected && !isMandatory && 'border-transparent',
        isUpdating && 'opacity-65'
      )}
    >
      {/* Icons (expired, accessories, quantity selector) */}
      {isIconsVisible && (
        <div className="absolute right-2.5 top-2.5 z-50 flex flex-col gap-1">
          {/* Quantity selector */}
          {Boolean(isSelected) && isSingleProductGroup && (
            <>
              {Boolean((product?.quantityLimit || 1) > 1) && (
                <div className="mt-1" id={`quantity-selector-enabled-${id}`}>
                  <UnfoldMoreHorizontalIcon />

                  <Tooltip
                    anchorId={`quantity-selector-enabled-${id}`}
                    content={translate('Quantity selector enabled')}
                    className="z-50"
                  />
                </div>
              )}
            </>
          )}

          {/* Accessories button */}
          {isGrouped && isSingleProductGroup && hasAccessories && (
            <Button
              id={`has-accessories-${id}`}
              size="extraSmall"
              variant="primary"
              Icon={<HeadphonesIcon className="mr-0" />}
              className="m-0 mb-2 w-full px-1"
              onClick={handleOnlySelectedProductAccessoriesClick}
            >
              <Tooltip
                anchorId={`has-accessories-${id}`}
                content={translate('See product accessories')}
                size="large"
                className="z-50"
              />
            </Button>
          )}

          {/* Expired Icon */}
          {isExpired && (
            <div
              id={`expired-info-${id}`}
              className={twJoin(
                fillExpiredColorByWhiteLabel[whiteLabel] || fillExpiredColorByWhiteLabel.skytech
              )}
            >
              <InformationCircleIcon
                color={twJoin(
                  fillExpiredColorByWhiteLabel[whiteLabel] || fillExpiredColorByWhiteLabel.skytech
                )}
              />

              <Tooltip
                anchorId={`expired-info-${id}`}
                content={translate(
                  isSingleProductGroup
                    ? 'This product has been discontinued by the supplier and can no longer be ordered'
                    : 'The group contains products that have been discontinued by the supplier'
                )}
                className="z-50"
              />
            </div>
          )}
        </div>
      )}

      <div className="flex w-full flex-col items-center">
        {/* Product image */}
        <button
          className="z-10 m-0 mb-4 flex h-28 w-full cursor-pointer appearance-none items-center justify-center overflow-hidden border-none bg-transparent p-0 transition hover:scale-125"
          onClick={e => {
            e.preventDefault()
            onMoreInfoClick()
          }}
        >
          <ImageWithIconFallback
            src={imgUrl}
            alt="product"
            className={twJoin('h-full max-w-44 transition', isUpdating && 'opacity-50')}
            fallbackIcon={<AssetFallbackIcon assetType={product?.assetType || 'OTHER'} />}
          />
        </button>

        <div className="w-full text-left">
          {/* Product category */}
          {!isSingleProductGroup && (
            <div className="mb-1 text-xs text-gray-500">
              <Highlight pattern={searchQuery} text={category} />
            </div>
          )}

          {/* Product/group name */}
          <button
            id={`product-${isGrouped ? `group-${id}` : String(id)}`}
            onClick={e => {
              e.preventDefault()
              onMoreInfoClick()
            }}
            type="button"
            className={twMerge(
              'm-0 w-full cursor-pointer appearance-none border-none bg-transparent p-0 text-left transition',
              isUpdating ? 'text-gray-500' : 'text-gray-800'
            )}
          >
            <StyledText
              colorMode="black"
              size="regular-plus"
              fontWeight="semibold"
              className="line-clamp-2 text-ellipsis transition"
            >
              <Highlight pattern={searchQuery} text={isGrouped ? name : alias || name} />
            </StyledText>

            <Tooltip
              anchorId={`product-${isGrouped ? `group-${id}` : String(id)}`}
              content={isGrouped ? name : alias || name}
              size="large"
              place="top"
              className="z-[9999]"
            />
          </button>

          {/* Product vendor unique ID / article number */}
          <div className="mt-0.75">
            <StyledText colorMode="gray" size="sm">
              {product?.sku ? (
                <Translation id="Vendor unique ID:" />
              ) : (
                <Translation id="Article No.:" />
              )}{' '}
              <Highlight pattern={searchQuery} text={product?.sku || product?.code} />
            </StyledText>
          </div>
        </div>
      </div>

      {/* Product cost */}
      <div className="mt-4 w-full text-left">
        <ProductCost
          isFromPriceTextVisible={!isSingleProductGroup && isFromPriceTextVisible}
          currency={currency}
          isVatIncluded={isVatIncluded}
          price={price}
          vatPct={isSingleProductGroup ? product!.vatPct : vatPct}
        />
      </div>

      <div className="mt-6 w-full">
        {/* Stock status */}
        <div className="mb-4 flex w-full flex-row items-center gap-1 text-left text-gray-500">
          {!inStock || parseInStock(inStock) === '0' ? (
            <>
              <span
                className={twMerge(
                  'flex size-2 flex-shrink-0 rounded-lg',
                  whiteLabel === 'threeStepIT' ? 'bg-threeStepIT-neutral' : 'bg-blue'
                )}
              />
              <Translation id="Available in stock" />
            </>
          ) : (
            <>
              <span
                className={twMerge(
                  'flex size-2 flex-shrink-0 rounded-lg',
                  inStockStatusStyle(inStock, whiteLabel)
                )}
              />
              {isGrouped ? inStock : parseInStock(inStock)} <Translation id="in stock" />
            </>
          )}
        </div>

        {/* Product actions */}
        <ProductFooter
          hasEditAccess={hasEditAccess}
          isSingleProductGroup={isSingleProductGroup}
          isSelected={isSelected}
          isAllSelected={selectedProductsNumber === groupedProducts?.length}
          isMandatory={isMandatory}
          tooltipId={`remove-product-tooltip-${id}`}
          handleLeftBtnClick={onSelectDeselectClick}
          handleRightBtnClick={onMoreInfoClick}
        />
      </div>

      {isUpdating && (
        <div className="absolute left-1/3 top-1/4">
          <LoaderCircle
            className={twMerge(
              cardLoaderCircleStyleByWhiteLabel[whiteLabel] ||
                cardLoaderCircleStyleByWhiteLabel.skytech
            )}
          />
        </div>
      )}

      {product && (
        <Popup
          ariaLabel={translate('Product information')}
          isOpen={isSettingsPopupOpen}
          onClose={() => setIsSettingsPopupOpen(false)}
        >
          <ProductMoreInfoOverview
            isSelected={isSelected}
            policyId={policyId}
            productId={Number(product.id)}
            onClose={() => setIsSettingsPopupOpen(false)}
          />
        </Popup>
      )}

      {!isSingleProductGroup && (
        <Popup
          variant="wide"
          isOpen={isProductsModalOpen}
          ariaLabel={translate('Modal with products')}
          onClose={() => setIsProductsModalOpen(false)}
        >
          <ProductGroupOverview
            groupKey={String(id)}
            groupName={name}
            groupedProducts={groupedProducts || []}
            description={description}
            hasAccessories={hasAccessories || false}
            isMandatory={isMandatory}
            onModalClose={() => setIsProductsModalOpen(false)}
          />
        </Popup>
      )}
    </Card>
  )
}

const ProductCost: FC<{
  isFromPriceTextVisible: boolean
  currency: string | null
  isVatIncluded: boolean
  price: number
  vatPct: number
}> = ({ isFromPriceTextVisible, currency, isVatIncluded, price, vatPct }) => (
  <>
    {isFromPriceTextVisible && (
      <StyledText size="large" colorMode="black">
        <Translation id="from" />{' '}
      </StyledText>
    )}
    <StyledText size="xl" colorMode="black" fontWeight="semibold">
      <FormattedNumberWithCurrency
        amount={isVatIncluded ? price * (1 + vatPct / 100) : price}
        currency={currency}
      />
    </StyledText>
  </>
)

const ProductFooter: FC<{
  hasEditAccess: boolean
  isSingleProductGroup: boolean
  isSelected: boolean
  isAllSelected: boolean
  isMandatory: boolean
  tooltipId: string
  handleLeftBtnClick: () => void
  handleRightBtnClick: () => void
}> = ({
  hasEditAccess,
  isSingleProductGroup,
  isSelected,
  isAllSelected,
  isMandatory,
  tooltipId,
  handleLeftBtnClick,
  handleRightBtnClick,
}) => {
  const translate = useTranslation()
  const leftBtnTxtId = isSingleProductGroup
    ? isSelected
      ? 'Remove'
      : 'Select'
    : isAllSelected
      ? 'Remove all'
      : 'Select all'
  const isDangerBtn =
    (isSingleProductGroup && isSelected) || (!isSingleProductGroup && isAllSelected)
  const disableLeftButton = isMandatory && isDangerBtn
  return (
    <div className="mobile:flex-row mobile:items-center flex w-full flex-col-reverse items-stretch justify-between gap-4 md:gap-2">
      <span
        data-tooltip-id={tooltipId}
        data-tooltip-content={translate(
          `A mandatory product cannot be removed. To enable removing, change the "Is mandatory" option and save`
        )}
      >
        <Button
          className="m-0 basis-1/4 md:px-2.5"
          size="medium"
          variant={isDangerBtn ? 'danger' : 'primary'}
          disabled={!hasEditAccess || disableLeftButton}
          Icon={isDangerBtn ? <></> : undefined}
          onClick={e => {
            e.preventDefault()
            handleLeftBtnClick()
          }}
        >
          <Translation id={leftBtnTxtId} />
        </Button>
      </span>
      {disableLeftButton && <Tooltip id={tooltipId} style={{ zIndex: 100 }} size="large" />}
      <Button
        className="m-0 basis-1/4 md:px-2.5"
        size="medium"
        Icon={isSingleProductGroup ? <ChevronRightIcon /> : undefined}
        onClick={e => {
          e.preventDefault()
          handleRightBtnClick()
        }}
      >
        {!isSingleProductGroup && <Translation id="Open group" />}
      </Button>
    </div>
  )
}
